import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { getAuth } from 'firebase/auth'
import { FiAlertTriangle, FiX } from 'react-icons/fi'
import Button from './ui/Button'
import Card from './ui/Card'
import { apiUrl } from '../config/api'

const CancelSubscriptionModal = ({ planName, accessUntilLabel, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !loading) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [loading, onClose])

  const handleConfirm = async () => {
    setLoading(true)
    try {
      const token = await getAuth().currentUser?.getIdToken()
      if (!token) throw new Error('Você precisa estar logado.')

      const resp = await fetch(apiUrl('/api/asaas/cancel-current'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      const raw = await resp.text()
      const data = (() => {
        try { return JSON.parse(raw) } catch { return null }
      })()
      if (!data) {
        throw new Error(`Resposta inválida do backend (não-JSON). Início: ${raw.slice(0, 60)}`)
      }
      if (!resp.ok || !data.ok) throw new Error(data.error || 'Erro ao cancelar.')

      const untilLabel = data.accessUntil
        ? new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(
            new Date(`${data.accessUntil}T00:00:00Z`)
          )
        : accessUntilLabel

      toast.success(
        data.alreadyCancelled
          ? `Renovação já estava cancelada. Acesso até ${untilLabel}. Para reativar depois, use Ir para o checkout e reativar em Minha conta.`
          : `Renovação cancelada. Acesso ao ${data.planName || planName} até ${untilLabel}. Para reativar depois, use Ir para o checkout e reativar em Minha conta.`
      )
      onSuccess?.()
      onClose()
    } catch (err) {
      console.error(err)
      toast.error(err.message || 'Erro ao cancelar.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-3 sm:p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
        onClick={loading ? undefined : onClose}
      />

      <div
        className="relative w-full max-w-md min-w-0"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-subscription-title"
      >
        <Card padding="lg" className="relative">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none min-h-[44px] min-w-[44px] flex items-center justify-center disabled:opacity-50"
            aria-label="Fechar"
          >
            <FiX className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </button>

          <div className="mb-5 pt-2 pr-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300">
                <FiAlertTriangle className="w-5 h-5" />
              </div>
              <p className="text-xs font-bold text-accent uppercase tracking-[0.2em]">Cancelar renovação</p>
            </div>
            <h2
              id="cancel-subscription-title"
              className="text-lg sm:text-xl font-extrabold text-gray-900 dark:text-white mb-2"
            >
              Cancelar renovação do plano {planName}?
            </h2>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              <li className="flex gap-2">
                <span className="text-accent font-bold">•</span>
                <span>
                  Você mantém acesso ao plano até{' '}
                  <strong className="text-gray-900 dark:text-white">{accessUntilLabel || 'o fim do ciclo pago'}</strong>.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-bold">•</span>
                <span>Novas cobranças no cartão serão encerradas.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-bold">•</span>
                <span>O cartão será removido da central de pagamentos por segurança.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-bold">•</span>
                <span>Após essa data, seu plano volta ao Gratuito automaticamente.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-bold">•</span>
                <span>
                  Para reativar depois, use <strong className="text-gray-900 dark:text-white">Minha conta</strong> →{' '}
                  <strong className="text-gray-900 dark:text-white">Ir para o checkout e reativar</strong> e cadastre um
                  novo cartão. Não é possível reativar com &quot;Atualizar plano&quot;.
                </span>
              </li>
            </ul>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              type="button"
              variant="secondary"
              size="md"
              className="w-full hover:bg-accent/5 dark:hover:bg-accent/10 transition-colors"
              disabled={loading}
              onClick={onClose}
            >
              Manter assinatura
            </Button>
            <Button
              type="button"
              variant="outline"
              size="md"
              className="w-full !text-red-600 dark:!text-red-400 !border-red-200 dark:!border-red-500/40 hover:!bg-red-50 dark:hover:!bg-red-500/15 hover:!text-red-700 dark:hover:!text-red-300 hover:!border-red-300 dark:hover:!border-red-400/60 transition-all"
              disabled={loading}
              onClick={handleConfirm}
            >
              {loading ? 'Cancelando...' : 'Confirmar cancelamento'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default CancelSubscriptionModal
